import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Callout, Card, Stepper } from "@promptgatewaysadmin/ui";
import { usePortalClient } from "../../api/PortalClientProvider.js";
import { useSubscriptionState, startCheckout } from "../../queries/hooks.js";
import { catalogMoney } from "../../components/format.js";
import { errorCopy } from "../../copy/errors.js";
import { toPortalError, useOnline } from "../../components/states.js";
import { readFunnel } from "../../auth/persistence.js";
import { useSession } from "../../auth/SessionProvider.js";
import { config } from "../../config.js";
import { qk } from "../../queries/keys.js";
import type { SubscriptionStatus } from "../../api/normalise.js";
import {
  getPlan,
  isKnownInterval,
  isKnownPlan,
  isPerSeat,
  minSeats,
  summaryAmount,
  type IntervalCode,
  type PlanCode,
} from "../../pricing.js";

/**
 * /checkout — the order summary.
 *
 * It renders with ZERO NETWORK: the catalog is a build-time constant and the plan/interval
 * come from query params, the sessionStorage funnel, or the org's own plan (the session the
 * shell already holds). It waits for the subscription the shell already reads, because an org
 * that already pays is shown its plan and "Manage billing" instead of a payment form; a
 * failed read degrades to the form (Billing answers 409 ALREADY_SUBSCRIBED regardless).
 *
 * The card form was deleted — this is the left half only. All card handling is Stripe's.
 */
export function Checkout() {
  const [params] = useSearchParams();
  const client = usePortalClient();
  const sub = useSubscriptionState();
  const online = useOnline();
  const session = useSession();
  const queryClient = useQueryClient();

  // Enterprise is sales-led and never self-serve, so it falls through to the "Choose a plan"
  // card rather than being pre-selected from the org.
  const orgPlan = session.plan === "enterprise" ? null : session.plan;
  const funnel = readFunnel();
  const planParam = params.get("plan") ?? funnel.plan ?? orgPlan ?? null;
  const intervalParam =
    params.get("interval") ?? funnel.interval ?? sub.data?.interval ?? "month";

  const plan: PlanCode | null = isKnownPlan(planParam) ? planParam : null;
  const interval: IntervalCode = isKnownInterval(intervalParam) ? intervalParam : "month";

  // Enterprise is redirected at route level and never reaches the summary, where the
  // contract would return 400. Belt and braces: if it somehow does, we send them to sales
  // rather than rendering a price we do not have.
  useEffect(() => {
    if (plan === "enterprise") window.location.assign(`${config.siteOrigin}/contact-sales`);
  }, [plan]);

  // The floor is the HIGHER of the plan's minimum and the seats already consumed. Team's
  // minimum is 2, and asking for fewer is a 400 SEATS_BELOW_MINIMUM from the contract — so
  // the stepper must not be able to express it.
  const consumed = sub.data?.seatCount ?? 1;
  const planMin = plan && plan !== "enterprise" ? minSeats(plan) : 1;
  const seatFloor = Math.max(consumed, planMin);
  const [seats, setSeats] = useState(seatFloor);
  useEffect(() => setSeats((s) => Math.max(s, seatFloor)), [seatFloor]);

  const [busy, setBusy] = useState(false);
  const [errKey, setErrKey] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  // Billing answered 409 ALREADY_SUBSCRIBED: the portal's view was stale (or the org is on a
  // Stripe-side trial, which reads as `trialing` like a local one). Billing is the authority.
  const [refused, setRefused] = useState(false);

  const amount = useMemo(
    () => (plan ? summaryAmount(plan, interval, seats) : null),
    [plan, interval, seats],
  );

  // A paying org never sees a payment form: a second Checkout would open a second Stripe
  // subscription. `trialing` is NOT here — a local no-card trial must be able to pay, and the
  // portal cannot tell it from a Stripe-side trial; Billing's 409 covers the latter.
  // Nothing until the status is known, or a paying org sees the payment form flash first. A
  // failed read falls through to the form: Billing still refuses a second subscription.
  if (sub.isPending) return null;
  const live = sub.data && PAYING.has(sub.data.status) ? sub.data : null;
  if (live || refused) {
    const shownPlan = live?.plan ?? plan;
    const shownInterval = live ? live.interval : interval;
    const name = shownPlan ? getPlan(shownPlan).displayName : null;
    const period = shownInterval === "year" ? "annual" : shownInterval === "month" ? "monthly" : null;
    return (
      <Card title="You're already subscribed">
        <p className="pg-card__body">
          {name
            ? `You're already subscribed to ${name}${period ? ` (${period})` : ""}.`
            : "You're already subscribed."}
        </p>
        <Link className="pg-btn pg-btn--primary" to="/billing">
          Manage billing
        </Link>
      </Card>
    );
  }

  if (!plan || plan === "enterprise") {
    // Unknown or absent plan means NO PRE-SELECTION, never a guess at what they wanted.
    return (
      <Card title="Choose a plan">
        <p className="pg-card__body">
          Pick a plan to continue. Nothing is charged until you confirm on the next screen.
        </p>
        <a className="pg-btn pg-btn--primary" href={`${config.siteOrigin}/pricing`}>
          See plans
        </a>
      </Card>
    );
  }

  const catalogPlan = getPlan(plan);
  const perSeat = isPerSeat(plan);

  return (
    <Card title={`Subscribe to ${catalogPlan.displayName}`}>
      {errKey && (
        <Callout tone="danger" role="alert">
          {errorCopy(errKey).body}
        </Callout>
      )}

      {perSeat && (
        <Stepper
          label="Seats"
          value={seats}
          min={seatFloor}
          max={500}
          onChange={setSeats}
          // A native stepper is permitted HERE ONLY: it writes nothing, it only shapes the
          // session request. The contract clamps server-side to at least the consumed count.
          help={
            consumed > planMin
              ? `At least ${consumed} — one for everyone currently on your team.`
              : planMin > 1
                ? `${catalogPlan.displayName} starts at ${planMin} seats.`
                : undefined
          }
        />
      )}

      <dl className="pg-summary">
        <div>
          <dt>
            {catalogPlan.displayName}
            {perSeat ? ` × ${seats}` : ""}
          </dt>
          <dd>{amount === null ? "—" : catalogMoney(amount)}</dd>
        </div>
        <div>
          <dt>14-day trial credit</dt>
          {/* Kept deliberately: it answers the question the customer actually has — am I
              being charged for the trial I just used? — and the answer is no. */}
          <dd>&minus;{catalogMoney(0)}</dd>
        </div>
        <div>
          <dt>Tax</dt>
          {/* NEVER $0.00. We cannot know tax before Stripe collects an address, and a
              confident $0.00 that becomes $216 of VAT on the next screen is the worst
              possible sequence. */}
          <dd>calculated at checkout</dd>
        </div>
        <div className="pg-summary__total">
          <dt id="total-label">Total before tax</dt>
          <dd>
            <strong id="total-amount">{amount === null ? "—" : catalogMoney(amount)}</strong>
          </dd>
        </div>
      </dl>

      <p className="pg-card__body">
        {plan === "individual" ? (
          <Link to="/checkout?plan=team">Switch to Team</Link>
        ) : (
          <Link to="/checkout?plan=individual">Switch to Individual</Link>
        )}
      </p>

      <Button
        block
        disabled={busy || amount === null}
        aria-disabled={!online || undefined}
        // The amount is announced with the action that charges it.
        aria-describedby="total-amount"
        onClick={async () => {
          if (!online || busy) return;
          setBusy(true); // locks on the FIRST click so two redirects never race
          setErrKey(null);
          setAnnouncement("Taking you to Stripe to pay securely.");
          try {
            // The server maps {plan, interval, addons} to fixed Stripe Price IDs. NO price
            // and NO amount is in this body — the server owns money.
            await startCheckout(client, { plan, interval, seats: perSeat ? seats : 1 });
          } catch (e) {
            const failure = toPortalError(e);
            if (failure?.contractCode === "ALREADY_SUBSCRIBED") {
              setRefused(true);
              setAnnouncement("");
              if (session.orgId) {
                void queryClient.invalidateQueries({ queryKey: qk.subscription(session.orgId) });
              }
            } else {
              setErrKey(failure?.copyKey ?? "error.checkout.invalid");
            }
            setBusy(false);
          }
        }}
      >
        {busy ? "Opening secure checkout…" : "Continue to payment"}
      </Button>

      {!online && (
        <p className="pg-disabled-reason">You&apos;re offline. Reconnect to continue.</p>
      )}

      <p className="pg-card__body">
        Payment is handled by Stripe. We never see or store your card.{" "}
        <Link to="/">Back to dashboard</Link>
      </p>

      {/* An unannounced full-page redirect is disorienting with a screen reader. */}
      <span className="pg-visually-hidden" role="status" aria-live="polite">
        {announcement}
      </span>
    </Card>
  );
}

const PAYING: ReadonlySet<SubscriptionStatus> = new Set(["active", "past_due", "incomplete"]);
